import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DollarSign, Plus, Pencil, Trash2, Loader2, Briefcase, FileText } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Help } from "@/pages/employer/aca/shared/Help";

interface CompensationRow {
  id: number;
  effectiveDate: string;
  compensationType: "hourly" | "salary" | null;
  amount: string | number | null;
  payCycle: string | null;
  scheduledHoursPerWeek: string | number | null;
  source: "manual" | "trigger" | "backfill" | "gusto" | "import" | string;
  sourceRef: string | null;
  reason: string | null;
  createdByUserId: string | null;
  createdAt: string | null;
}

interface EmploymentPeriod {
  id: number;
  startDate: string;
  endDate: string | null;
  source: string;
  notes: string | null;
}

interface CompensationPayload {
  history: CompensationRow[];
  current: CompensationRow | null;
  employmentPeriods: EmploymentPeriod[];
}

interface W2Row {
  taxYear: number;
  box1Wages: string | number | null;
  isFinal: boolean;
  source: string;
  enteredAt: string | null;
}

interface CompensationHistoryCardProps {
  employeeId: number;
  canEdit: boolean;
  compact?: boolean;
}

const PAY_CYCLES = ["Weekly", "Bi-Weekly", "Semi-Monthly", "Monthly"];

const todayIso = () => new Date().toISOString().slice(0, 10);

const money = (v: string | number | null | undefined) =>
  v == null || v === ""
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(Number(v));

const amountLabel = (row: { compensationType: string | null; amount: string | number | null }) =>
  row.amount == null ? "—" : `${money(row.amount)}${row.compensationType === "hourly" ? "/hr" : row.compensationType === "salary" ? "/yr" : ""}`;

const fmtDate = (d: string | null | undefined) => {
  if (!d) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(d);
};

const sourceLabel = (s: string): { label: string; variant: "default" | "secondary" | "outline" } => {
  switch (s) {
    case "gusto": return { label: "Payroll (Gusto)", variant: "default" };
    case "backfill": return { label: "Backfilled", variant: "outline" };
    case "import": return { label: "Import", variant: "secondary" };
    case "trigger": return { label: "Employee record", variant: "secondary" };
    default: return { label: "Manual", variant: "secondary" };
  }
};

type FormState = {
  effectiveDate: string;
  compensationType: "hourly" | "salary";
  amount: string;
  payCycle: string;
  scheduledHoursPerWeek: string;
  reason: string;
};

const emptyForm = (current: CompensationRow | null): FormState => ({
  effectiveDate: todayIso(),
  compensationType: (current?.compensationType as "hourly" | "salary") || "hourly",
  amount: "",
  payCycle: current?.payCycle || "",
  scheduledHoursPerWeek: current?.scheduledHoursPerWeek != null ? String(current.scheduledHoursPerWeek) : "",
  reason: "",
});

export function CompensationHistoryCard({ employeeId, canEdit, compact = false }: CompensationHistoryCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const compKey = ["/api/employees", employeeId, "compensation"];
  const w2Key = ["/api/employees", employeeId, "w2-wages"];

  const { data, isLoading } = useQuery<CompensationPayload>({
    queryKey: compKey,
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeId}/compensation`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load compensation history");
      return res.json();
    },
    enabled: !!employeeId,
  });

  const { data: w2Rows = [] } = useQuery<W2Row[]>({
    queryKey: w2Key,
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeId}/w2-wages`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load W-2 wages");
      return res.json();
    },
    enabled: !!employeeId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: compKey });
    queryClient.invalidateQueries({ queryKey: w2Key });
    queryClient.invalidateQueries({ queryKey: [`/api/employees/${employeeId}`] });
    queryClient.invalidateQueries({ queryKey: ["/api/employees", employeeId] });
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm(null));

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm(data?.current ?? null));
    setDialogOpen(true);
  };
  const openEdit = (row: CompensationRow) => {
    setEditingId(row.id);
    setForm({
      effectiveDate: String(row.effectiveDate).slice(0, 10),
      compensationType: (row.compensationType as "hourly" | "salary") || "hourly",
      amount: row.amount == null ? "" : String(row.amount),
      payCycle: row.payCycle || "",
      scheduledHoursPerWeek: row.scheduledHoursPerWeek == null ? "" : String(row.scheduledHoursPerWeek),
      reason: row.reason || "",
    });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        effectiveDate: form.effectiveDate,
        compensationType: form.compensationType,
        amount: form.amount,
        payCycle: form.payCycle || null,
        scheduledHoursPerWeek: form.scheduledHoursPerWeek === "" ? null : form.scheduledHoursPerWeek,
        reason: form.reason || null,
      };
      const res = editingId == null
        ? await apiRequest("POST", `/api/employees/${employeeId}/compensation`, body)
        : await apiRequest("PUT", `/api/employees/${employeeId}/compensation/${editingId}`, body);
      return res.json();
    },
    onSuccess: () => {
      const future = form.effectiveDate > todayIso();
      toast({
        title: editingId == null ? "Compensation change recorded" : "Compensation row updated",
        description: future
          ? `Scheduled: becomes the current amount on ${fmtDate(form.effectiveDate)}.`
          : "This is now the employee's current compensation.",
      });
      setDialogOpen(false);
      invalidate();
    },
    onError: (e: any) => toast({ title: "Save failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (rowId: number) => {
      const res = await apiRequest("DELETE", `/api/employees/${employeeId}/compensation/${rowId}`);
      return res.json();
    },
    onSuccess: () => { toast({ title: "Compensation row deleted" }); invalidate(); },
    onError: (e: any) => toast({ title: "Delete failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  // W-2 wages
  const currentYear = new Date().getFullYear();
  const w2Years = useMemo(() => {
    const ys = new Set<number>([currentYear - 1, currentYear - 2, ...w2Rows.map((r) => r.taxYear)]);
    return Array.from(ys).sort((a, b) => b - a);
  }, [w2Rows, currentYear]);
  const [w2Draft, setW2Draft] = useState<Record<number, { box1Wages: string; isFinal: boolean }>>({});
  const w2Value = (year: number) => {
    const d = w2Draft[year];
    const existing = w2Rows.find((r) => r.taxYear === year);
    return {
      box1Wages: d?.box1Wages ?? (existing?.box1Wages == null ? "" : String(existing.box1Wages)),
      isFinal: d?.isFinal ?? !!existing?.isFinal,
    };
  };
  const w2Mutation = useMutation({
    mutationFn: async (year: number) => {
      const v = w2Value(year);
      const res = await apiRequest("PUT", `/api/employees/${employeeId}/w2-wages`, {
        taxYear: year,
        box1Wages: v.box1Wages === "" ? null : v.box1Wages,
        isFinal: v.isFinal,
      });
      return res.json();
    },
    onSuccess: (_d, year) => {
      toast({ title: `W-2 wages saved for ${year}` });
      setW2Draft((prev) => { const n = { ...prev }; delete n[year]; return n; });
      invalidate();
    },
    onError: (e: any) => toast({ title: "Save failed", description: e?.message || "Unknown error", variant: "destructive" }),
  });

  const current = data?.current ?? null;
  const history = data?.history ?? [];
  const periods = data?.employmentPeriods ?? [];

  return (
    <Card>
      <CardHeader className={compact ? "pb-3" : undefined}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4" />
              Compensation history
              <Help term="compensationHistory" />
            </CardTitle>
            <CardDescription>
              Effective-dated pay changes. The ACA rate-of-pay safe harbor reads the rate in effect each month.
            </CardDescription>
          </div>
          {canEdit && (
            <Button type="button" size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" />
              Add change
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Current */}
            <div className="rounded-md border p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Current compensation</div>
              {current ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Amount</div>
                    <div className="font-medium">{amountLabel(current)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Type</div>
                    <div className="font-medium capitalize">{current.compensationType || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Pay cycle</div>
                    <div className="font-medium">{current.payCycle || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Scheduled hrs/week</div>
                    <div className="font-medium">{current.scheduledHoursPerWeek ?? "—"}</div>
                  </div>
                  <div className="col-span-2 md:col-span-4 text-xs text-muted-foreground">
                    Effective since {fmtDate(current.effectiveDate)}
                    {" · "}
                    <Badge variant={sourceLabel(current.source).variant} className="text-[10px]">{sourceLabel(current.source).label}</Badge>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No compensation on file.</p>
              )}
            </div>

            {/* History */}
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">History</div>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground">No history yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Effective</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Amount</TableHead>
                        {!compact && <TableHead>Pay cycle</TableHead>}
                        {!compact && <TableHead>Hrs/week</TableHead>}
                        <TableHead>Source</TableHead>
                        {!compact && <TableHead>Reason</TableHead>}
                        {canEdit && <TableHead className="w-[80px]" />}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((row) => {
                        const src = sourceLabel(row.source);
                        const future = String(row.effectiveDate).slice(0, 10) > todayIso();
                        const isCurrent = current?.id === row.id;
                        return (
                          <TableRow key={row.id}>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(row.effectiveDate)}
                              {isCurrent && <Badge className="ml-2 text-[10px]">Current</Badge>}
                              {future && <Badge variant="outline" className="ml-2 text-[10px]">Scheduled</Badge>}
                            </TableCell>
                            <TableCell className="capitalize">{row.compensationType || "—"}</TableCell>
                            <TableCell className="whitespace-nowrap">{amountLabel(row)}</TableCell>
                            {!compact && <TableCell>{row.payCycle || "—"}</TableCell>}
                            {!compact && <TableCell>{row.scheduledHoursPerWeek ?? "—"}</TableCell>}
                            <TableCell><Badge variant={src.variant} className="text-[10px]">{src.label}</Badge></TableCell>
                            {!compact && <TableCell className="max-w-[220px] truncate text-muted-foreground">{row.reason || ""}</TableCell>}
                            {canEdit && (
                              <TableCell>
                                {row.source === "manual" && (
                                  <div className="flex gap-1">
                                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(row)} aria-label="Edit">
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button type="button"
                                      variant="ghost" size="icon" className="h-7 w-7"
                                      disabled={history.length <= 1 || deleteMutation.isPending}
                                      onClick={() => deleteMutation.mutate(row.id)}
                                      aria-label="Delete"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Employment periods */}
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
                <Briefcase className="h-3.5 w-3.5" /> Employment periods <Help term="employmentPeriods" />
              </div>
              {periods.length === 0 ? (
                <p className="text-sm text-muted-foreground">No employment periods recorded.</p>
              ) : (
                <ul className="text-sm space-y-1">
                  {periods.map((p) => (
                    <li key={p.id} className="flex items-center gap-2">
                      <span>{fmtDate(p.startDate)} – {p.endDate ? fmtDate(p.endDate) : "present"}</span>
                      {!p.endDate && <Badge variant="secondary" className="text-[10px]">Active</Badge>}
                      {p.notes && <span className="text-muted-foreground">· {p.notes}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* W-2 wages */}
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" /> W-2 wages <Help term="w2Wages" />
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Used by the ACA W-2 affordability safe harbor; Pending Data until final.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Tax year</TableHead>
                    <TableHead>Box 1 wages</TableHead>
                    <TableHead className="w-[90px]">Final</TableHead>
                    {canEdit && <TableHead className="w-[90px]" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {w2Years.map((year) => {
                    const v = w2Value(year);
                    const dirty = !!w2Draft[year];
                    const existing = w2Rows.find((r) => r.taxYear === year);
                    return (
                      <TableRow key={year}>
                        <TableCell>{year}</TableCell>
                        <TableCell>
                          {canEdit ? (
                            <Input
                              type="number" step="0.01" min={0}
                              value={v.box1Wages}
                              placeholder="Pending"
                              className="h-8 max-w-[180px]"
                              onChange={(e) => setW2Draft((prev) => ({ ...prev, [year]: { box1Wages: e.target.value, isFinal: v.isFinal } }))}
                            />
                          ) : (
                            <span className="text-sm">{existing?.box1Wages == null ? <Badge variant="outline">Pending Data</Badge> : money(existing.box1Wages)}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {canEdit ? (
                            <Checkbox
                              checked={v.isFinal}
                              onCheckedChange={(c) => setW2Draft((prev) => ({ ...prev, [year]: { box1Wages: v.box1Wages, isFinal: c === true } }))}
                              aria-label={`Final ${year}`}
                            />
                          ) : existing?.isFinal ? <Badge className="text-[10px]">Final</Badge> : <span className="text-xs text-muted-foreground">No</span>}
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button type="button" size="sm" variant="outline" disabled={!dirty || w2Mutation.isPending} onClick={() => w2Mutation.mutate(year)}>
                              Save
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId == null ? "Add compensation change" : "Edit compensation row"}</DialogTitle>
            <DialogDescription>
              A change effective today or earlier becomes the current amount immediately. A future date is scheduled and takes effect on that day.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="comp-effective" className="flex items-center gap-1">Effective date <Help text="The first day the new amount applies. The ACA rate-of-pay safe harbor uses the rate in effect in each month, so a raise effective mid-month counts from that month on; a future date is scheduled and becomes current on that day." /></Label>
              <Input id="comp-effective" type="date" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.compensationType} onValueChange={(v) => setForm({ ...form, compensationType: v as "hourly" | "salary" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="salary">Salary (annual)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="comp-amount">{form.compensationType === "hourly" ? "Hourly rate" : "Annual salary"}</Label>
              <Input id="comp-amount" type="number" step="0.01" min={0} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Pay cycle</Label>
              <Select value={form.payCycle || "__none"} onValueChange={(v) => setForm({ ...form, payCycle: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Not set</SelectItem>
                  {PAY_CYCLES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="comp-hours">Scheduled hours / week</Label>
              <Input id="comp-hours" type="number" step="0.5" min={0} max={168} value={form.scheduledHoursPerWeek} onChange={(e) => setForm({ ...form, scheduledHoursPerWeek: e.target.value })} />
            </div>
            <div className="space-y-1 col-span-2">
              <Label htmlFor="comp-reason">Reason</Label>
              <Input id="comp-reason" placeholder="e.g. Annual raise, promotion" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="button"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !form.effectiveDate || form.amount === ""}
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId == null ? "Record change" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default CompensationHistoryCard;
